/**
 * Il rinnovo del biglietto di coda (qTournaments 1.72.0, kb#282): e' lui a dire al motore che il
 * giocatore c'e'. Senza rinnovo per `expiresAfterMs` (~45 s) si esce da soli.
 *
 * 🔴 **SOLO IN PRIMO PIANO, ed e' la regola che tiene onesta la coda.** Un rinnovo in background
 * terrebbe in coda chi ha chiuso l'app, e l'avversario abbinato con lui aspetterebbe fino allo
 * scadere del check-in (kb#282, «Per tma-orc»). ⇒ A ogni giro si RICHIEDE la visibilita': nascosta,
 * il giro passa senza rinnovare; tornata visibile, si rinnova SUBITO — il biglietto puo' essere
 * scaduto nel frattempo, e la risposta lo dice (`exitReason: 'rinnovo-scaduto'`), che e' una frase
 * e non un errore.
 * ⚠️ Il segnale e' `visibilitychange`: Telegram non ha un evento «activated» da ascoltare
 * (misurato da tma-orcb-1-qsocials il 2026-09-25).
 * ⚠️ **E l'evento non e' la fonte della verita', e' un acceleratore**: il timer gira anche a app
 * nascosta (e il browser lo rallenta da se'), e a ogni scatto guarda `isVisible()`. Cosi' una
 * WebView che cambia visibilita' SENZA mandare l'evento non rinnova da nascosta, e da visibile
 * riprende al giro successivo invece di restare ferma finche' un evento non arriva.
 *
 * ⛔ Non e' un `pollingInterval` di RTK (vietato nelle schermate Limulo, `verify-limulo.ts` §22):
 * il ritmo lo decide il biglietto a ogni giro (`renewDelayOf`), e da abbinati cambia.
 *
 * Puro: timer, visibilita' e chiamata sono parametri, cosi' il comportamento nel tempo si prova
 * con timer finti (`tests/code-tornei-rinnovo.test.ts`).
 */
import { RENEW_FALLBACK_MS, renewDelayOf, type LimuloQueueTicket } from './code.ts';

export interface RenewLoopDeps<H> {
  /** Una chiamata a `tournament.queue.renew`. Il biglietto risponde SEMPRE, anche chiuso. */
  renew: () => Promise<{ ticket: LimuloQueueTicket | null; error: unknown }>;
  isVisible: () => boolean;
  /** Si iscrive ai cambi di visibilita'; ritorna la disiscrizione. */
  onVisibilityChange: (listener: () => void) => () => void;
  setTimer: (fn: () => void, ms: number) => H;
  clearTimer: (handle: H) => void;
  onTicket: (ticket: LimuloQueueTicket) => void;
  onError?: (error: unknown) => void;
}

export interface RenewLoop {
  stop: () => void;
  /**
   * Rinnova ADESSO, senza aspettare il giro: per chi ha la prova che il biglietto puo' essere cambiato
   * (un torneo nuovo in `/mine`). E' lo stesso gesto del ritorno in primo piano: da nascosta non fa niente.
   */
  ora: () => void;
}

/**
 * Avvia il rinnovo a partire dall'ultimo biglietto noto (quello di `join`, o dell'ultima lettura).
 * Si ferma da solo quando il biglietto e' chiuso (`idle`), o con `stop()`.
 * `subito`: il primo giro parte ORA e non dopo `renewEveryMs`. Serve per un biglietto RIPRESO da una
 * lista, che non dice quando e' stato rinnovato l'ultima volta: aspettare altri 20 s puo' voler dire
 * arrivare dopo `expiresAfterMs` (~45 s) e trovarsi fuori dalla coda (revisione di #419, punto 4).
 */
export function startRenewLoop<H>(
  deps: RenewLoopDeps<H>,
  first: LimuloQueueTicket,
  { subito = false }: { subito?: boolean } = {},
): RenewLoop {
  let stopped = false;
  let inFlight = false;
  let ancora = false;
  let timer: H | null = null;
  let last = first;

  const clear = () => {
    if (timer !== null) deps.clearTimer(timer);
    timer = null;
  };

  const schedule = (ms: number) => {
    clear();
    timer = deps.setTimer(() => {
      timer = null;
      void tick();
    }, ms);
  };

  const stop = () => {
    stopped = true;
    clear();
    unsubscribe();
  };

  async function tick(): Promise<void> {
    if (stopped) return;
    // Un `ora()` mentre un rinnovo e' in volo non si perde: la prova e' piu' nuova della risposta che
    // arrivera', e appena arriva si rinnova di nuovo (revisione di #433, punto 8).
    if (inFlight) {
      ancora = true;
      return;
    }
    // Nascosta: il giro passa senza rinnovare, e si riguarda al prossimo.
    if (!deps.isVisible()) {
      schedule(renewDelayOf(last) ?? RENEW_FALLBACK_MS);
      return;
    }
    inFlight = true;
    let result: { ticket: LimuloQueueTicket | null; error: unknown };
    try {
      result = await deps.renew();
    } catch (error) {
      result = { ticket: null, error };
    }
    inFlight = false;
    // Una risposta arrivata dopo `stop()` non si consegna: chi l'aveva chiesta non c'e' piu'.
    if (stopped) return;
    if (result.ticket !== null) {
      last = result.ticket;
      deps.onTicket(result.ticket);
    } else {
      deps.onError?.(result.error);
    }
    const next = renewDelayOf(last);
    if (next === null) {
      stop();
      return;
    }
    if (ancora) {
      ancora = false;
      void tick();
      return;
    }
    // Un giro fallito non ferma il rinnovo: il prossimo arriva al ritmo di sempre, e se nel
    // frattempo il motore ci ha fatto uscire, la risposta lo dira'.
    schedule(next);
  }

  // Tornati in primo piano: subito, senza aspettare il giro. Nascosti: niente da fare, il giro
  // che scatta lo vede da se'.
  const ora = () => {
    if (stopped || !deps.isVisible()) return;
    clear();
    void tick();
  };
  const unsubscribe = deps.onVisibilityChange(ora);

  const firstDelay = renewDelayOf(first);
  if (firstDelay === null) stop();
  else schedule(subito ? 0 : firstDelay);

  return { stop, ora };
}
